/**
 * LogsTab — #56 PR 3b. Operations → Raw logs: the templated CloudWatch Logs Insights lookup
 * (POST /v1/platform/ops/logs/queries, then GET …/:id about once a second until it is done).
 *
 * One search box takes a refCode (ERR-XXXX), an error id (err_…) or a request id (req_…);
 * a tenant's warnings and errors are opened from the Errors tab, never typed. The server
 * builds the query from a fixed template and returns a fixed set of fields; nothing typed
 * here becomes query text. A search that runs past 20 s is stopped.
 */
import * as React from 'react';
import { useApiMutation, useApiOpts, useApiQuery } from '@lodex-pro/web-shared';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from 'src/components/ui/table';
import { Button } from 'src/components/ui/button';
import { Pill, fieldStyle, labelStyle, searchInputStyle, subText } from '../../ui/bits';
import {
  LOG_QUERIES_PATH,
  type LogQueryResults,
  type LogQueryStart,
  type LogTemplate,
} from './hooks';
import { logLevelLabel, logLevelTone, parseSearch } from './labels';

/** What the Errors tab hands over when it opens a raw log. */
export interface LogTarget {
  readonly template: LogTemplate;
  readonly value: string;
  /** Shown instead of the value (a tenant's name, never its id). */
  readonly label?: string;
  readonly from?: string;
  readonly to?: string;
}

const POLL_MS = 1_000;
export const LOG_SEARCH_TIMEOUT_MS = 20_000;

const SPANS: ReadonlyArray<{ id: string; label: string; ms: number }> = [
  { id: '15m', label: 'Last 15 minutes', ms: 15 * 60_000 },
  { id: '1h', label: 'Last hour', ms: 60 * 60_000 },
  { id: '6h', label: 'Last 6 hours', ms: 6 * 60 * 60_000 },
  { id: '24h', label: 'Last 24 hours', ms: 24 * 60 * 60_000 },
];

type Body = {
  template: LogTemplate;
  value: string;
  from?: string;
  to?: string;
  includeStack?: boolean;
};

function errMessage(e: unknown): string {
  const code = (e as { code?: string | null } | null)?.code;
  if (code === 'RATE_LIMIT_EXCEEDED') return 'Too many searches. Wait a minute and try again.';
  if (code === 'OPS_LOG_LOOKUP_UNAVAILABLE') {
    return 'Raw logs are not available right now. The Errors tab still works.';
  }
  if (code === 'VALIDATION_FAILED')
    return 'That search is not valid. Check the id and the time range.';
  return 'The search could not be started.';
}

function ResultsTable({ r }: { r: LogQueryResults }): React.JSX.Element {
  if (r.rows.length === 0) {
    return <p style={{ fontSize: '14px', marginTop: '12px' }}>No log lines match.</p>;
  }
  return (
    <Table aria-label="Log lines">
      <TableHeader>
        <TableRow>
          <TableHead>When</TableHead>
          <TableHead>Level</TableHead>
          <TableHead>What</TableHead>
          <TableHead>Request</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {r.rows.map((row, i) => {
          const at = row['@timestamp'];
          const level = row['level'];
          const status = row['res.statusCode'] ?? row['originalStatus'];
          return (
            <TableRow key={`${at ?? ''}-${String(i)}`}>
              <TableCell>
                {at !== undefined && at !== '' ? new Date(at).toLocaleString() : '—'}
              </TableCell>
              <TableCell>
                {level !== undefined && (
                  <Pill label={logLevelLabel(level)} tone={logLevelTone(level)} />
                )}
              </TableCell>
              <TableCell>
                <div>{row['msg'] ?? row['event'] ?? ''}</div>
                <div style={subText}>
                  {[row['event'], row['code'], row['refCode']]
                    .filter((x) => x !== undefined && x !== '')
                    .join(' · ')}
                </div>
                {row['err.message'] !== undefined && (
                  <div style={{ fontSize: '13px', marginTop: '4px' }}>{row['err.message']}</div>
                )}
                {row['err.stack'] !== undefined && (
                  <pre
                    aria-label="Stack trace"
                    style={{ fontSize: '11px', whiteSpace: 'pre-wrap', margin: '4px 0 0' }}
                  >
                    {row['err.stack']}
                  </pre>
                )}
              </TableCell>
              <TableCell>
                {row['req.method'] !== undefined && (
                  <div style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                    {row['req.method']} {row['req.url'] ?? ''}
                  </div>
                )}
                <div style={subText}>
                  {[
                    status,
                    row['responseTime'] !== undefined ? `${row['responseTime']} ms` : undefined,
                  ]
                    .filter((x) => x !== undefined)
                    .join(' · ')}
                </div>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

export function LogsTab({ initial }: { initial?: LogTarget | null }): React.JSX.Element {
  const opts = useApiOpts();
  const [search, setSearch] = React.useState(
    initial !== undefined && initial !== null && initial.template !== 'tenant' ? initial.value : '',
  );
  const [tenant, setTenant] = React.useState<LogTarget | null>(
    initial?.template === 'tenant' ? initial : null,
  );
  // A window handed over by the Errors tab, until the admin picks a span instead.
  const [fixed, setFixed] = React.useState<{ from: string; to: string } | null>(
    initial?.from !== undefined && initial.to !== undefined
      ? { from: initial.from, to: initial.to }
      : null,
  );
  const [span, setSpan] = React.useState('1h');
  const [includeStack, setIncludeStack] = React.useState(false);
  const [running, setRunning] = React.useState<{ start: LogQueryStart; at: number } | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [now, setNow] = React.useState(() => Date.now());
  // Poll while a search is in flight; off once it is done, failed or timed out.
  const [polling, setPolling] = React.useState(false);

  const target = parseSearch(search);
  const template: LogTemplate | null =
    tenant !== null
      ? 'tenant'
      : target.kind === 'refCode' || target.kind === 'errorId' || target.kind === 'requestId'
        ? target.kind
        : null;

  const start = useApiMutation<Body, LogQueryStart>(opts, LOG_QUERIES_PATH, 'POST');
  const stopPath = `${LOG_QUERIES_PATH}/${running?.start.id ?? '-'}`;
  const stop = useApiMutation<Record<string, never>, unknown>(opts, stopPath, 'DELETE');

  const elapsed = running === null ? 0 : now - running.at;
  const results = useApiQuery<LogQueryResults>(opts, stopPath, {
    queryKey: ['platform', 'ops', 'logs', running?.start.id ?? ''],
    enabled: running !== null,
    retry: false,
    refetchInterval: polling ? POLL_MS : false,
  });
  const r = running === null ? undefined : results.data;
  const done = r !== undefined && r.status !== 'Running' && r.status !== 'Scheduled';
  const timedOut = running !== null && !done && elapsed >= LOG_SEARCH_TIMEOUT_MS;

  React.useEffect(() => {
    if (done || timedOut || results.isError) setPolling(false);
  }, [done, timedOut, results.isError]);

  // A clock for the "Searching… n s" line and the timeout, only while a search runs.
  React.useEffect(() => {
    if (running === null || done) return undefined;
    const t = setInterval(() => setNow(Date.now()), 500);
    return (): void => clearInterval(t);
  }, [running, done]);

  // Past 20 s, stop the query (it still costs while it scans) and say so.
  React.useEffect(() => {
    if (timedOut) stop.mutate({});
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once, when it times out
  }, [timedOut]);

  const run = (): void => {
    if (template === null) return;
    const value =
      tenant !== null
        ? tenant.value
        : target.kind === 'none' || target.kind === 'invalid'
          ? ''
          : target.value;
    const window =
      fixed ??
      (() => {
        const to = Date.now();
        const ms = SPANS.find((s) => s.id === span)?.ms ?? 60 * 60_000;
        return { from: new Date(to - ms).toISOString(), to: new Date(to).toISOString() };
      })();
    setError(null);
    setRunning(null);
    start.mutate(
      {
        template,
        value,
        ...window,
        ...(template === 'errorId' && includeStack ? { includeStack: true } : {}),
      },
      {
        onSuccess: (res: LogQueryStart) => {
          const at = Date.now();
          setNow(at);
          setRunning({ start: res, at });
          setPolling(true);
        },
        onError: (e: unknown) => setError(errMessage(e)),
      },
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        {tenant === null ? (
          <label style={{ flex: 2, minWidth: '240px' }}>
            <span style={labelStyle}>Find in the raw logs</span>
            <input
              aria-label="Find in the raw logs by refCode, error id or request id"
              placeholder="ERR-XXXX, err_… or req_…"
              value={search}
              onChange={(ev): void => setSearch(ev.target.value)}
              style={searchInputStyle}
            />
          </label>
        ) : (
          <p style={{ flex: 2, fontSize: '14px', margin: 0 }}>
            Warnings and errors for <strong>{tenant.label ?? 'this tenant'}</strong>{' '}
            <button
              type="button"
              onClick={(): void => setTenant(null)}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--color-link)',
                cursor: 'pointer',
              }}
            >
              Clear
            </button>
          </p>
        )}
        <label style={{ flex: 1, minWidth: '170px' }}>
          <span style={labelStyle}>When</span>
          <select
            aria-label="Log time range"
            value={fixed === null ? span : 'fixed'}
            onChange={(ev): void => {
              setFixed(null);
              setSpan(ev.target.value);
            }}
            style={fieldStyle}
          >
            {fixed !== null && (
              <option value="fixed">
                {new Date(fixed.from).toLocaleTimeString()}–
                {new Date(fixed.to).toLocaleTimeString()}
              </option>
            )}
            {SPANS.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label}
              </option>
            ))}
          </select>
        </label>
        <label style={{ display: 'flex', gap: '6px', alignItems: 'center', fontSize: '13px' }}>
          <input
            type="checkbox"
            checked={includeStack && template === 'errorId'}
            disabled={template !== 'errorId'}
            onChange={(ev): void => setIncludeStack(ev.target.checked)}
          />
          Include the stack trace
        </label>
        <Button size="sm" disabled={template === null || start.isPending} onClick={run}>
          Search logs
        </Button>
      </div>

      {tenant === null && target.kind === 'invalid' && (
        <p role="alert" style={{ ...subText, color: 'var(--color-warning)' }}>
          Enter a refCode (ERR-XXXX), an error id (err_…) or a request id (req_…).
        </p>
      )}
      <p style={{ ...subText, textTransform: 'none' }}>
        Searches the API&apos;s own logs (kept 30 days), at most 24 hours at a time. Each search is
        recorded.
      </p>
      {error !== null && (
        <p role="alert" style={{ fontSize: '14px' }}>
          {error}
        </p>
      )}

      {running !== null && (
        <div role="status" style={{ fontSize: '14px', margin: '12px 0' }}>
          {timedOut
            ? 'The search took too long and was stopped. Try a shorter time range.'
            : results.isError
              ? 'Raw logs are not available right now.'
              : !done
                ? `Searching… ${String(Math.floor(elapsed / 1000))} s`
                : r.status === 'Complete'
                  ? `${String(r.rows.length)} line(s)${
                      r.bytesScanned === null
                        ? ''
                        : `, ${String(Math.ceil(r.bytesScanned / 1024))} KB searched`
                    }`
                  : `The search ended without results (${r.status}).`}
        </div>
      )}
      {done && r.status === 'Complete' && !timedOut && <ResultsTable r={r} />}
    </div>
  );
}
